// Pick the right glyph for an alert's event type. Mobile uses Ionicons
// (water-outline / car-outline / shield / flame / cloud); web uses our
// own SVG icon set, with a generic AlertIcon as the catch-all.

import type { CSSProperties } from 'react';

import { AlertIcon } from '../icons';

interface EventIconProps {
  readonly eventType: string | null | undefined;
  readonly size?: number;
  readonly color?: string;
  readonly style?: CSSProperties;
}

const sharedSvg = (size: number, color: string, style?: CSSProperties) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: color,
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  focusable: false,
  style,
});

function WaterIcon({ size, color, style }: { size: number; color: string; style?: CSSProperties }) {
  return (
    <svg {...sharedSvg(size, color, style)}>
      <path d="M12 3c-3 4-6 7-6 11a6 6 0 0012 0c0-4-3-7-6-11z" />
    </svg>
  );
}

function CarIcon({ size, color, style }: { size: number; color: string; style?: CSSProperties }) {
  return (
    <svg {...sharedSvg(size, color, style)}>
      <path d="M5 13l1.6-4.5A2 2 0 018.5 7h7a2 2 0 011.9 1.5L19 13" />
      <path d="M3 13h18v5h-3v2h-2v-2H8v2H6v-2H3v-5z" />
      <circle cx="7.5" cy="16" r="1" fill={color} />
      <circle cx="16.5" cy="16" r="1" fill={color} />
    </svg>
  );
}

function ShieldIcon({ size, color, style }: { size: number; color: string; style?: CSSProperties }) {
  return (
    <svg {...sharedSvg(size, color, style)}>
      <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

function FireIcon({ size, color, style }: { size: number; color: string; style?: CSSProperties }) {
  return (
    <svg {...sharedSvg(size, color, style)}>
      <path d="M12 3c1 3 4 4 4 8a4 4 0 11-8 0c0-2 1-3 1-5 0-1 1-2 3-3z" />
    </svg>
  );
}

function CloudIcon({ size, color, style }: { size: number; color: string; style?: CSSProperties }) {
  return (
    <svg {...sharedSvg(size, color, style)}>
      <path d="M7 18a4 4 0 010-8 5 5 0 0110-1 4 4 0 01-1 9H7z" />
    </svg>
  );
}

export function EventIcon({ eventType, size = 16, color = 'currentColor', style }: EventIconProps) {
  const e = (eventType ?? '').toLowerCase();
  if (e.includes('flood') || e.includes('water')) return <WaterIcon size={size} color={color} style={style} />;
  if (e.includes('traffic')) return <CarIcon size={size} color={color} style={style} />;
  if (e.includes('police')) return <ShieldIcon size={size} color={color} style={style} />;
  if (e.includes('fire')) return <FireIcon size={size} color={color} style={style} />;
  if (e.includes('weather') || e.includes('storm') || e.includes('snow')) return <CloudIcon size={size} color={color} style={style} />;
  return <AlertIcon size={size} stroke={2} style={style} />;
}
